import { apiRequest, apiRequestMaybeContent, apiRequestNoContent } from '@lib/api';
import { z } from 'zod';

import { Component, ComponentListResponse } from '../schemas/component';
import {
    ComponentSettingsView,
    type TenantComponentHolding,
    TenantComponentsView,
} from '../schemas/component-settings';

/**
 * The component catalog API, one function per route.
 *
 * Five routes today, and they sit at different tiers server-side: the two reads
 * are `read-only` — the catalog names capabilities rather than customers, and a
 * museum's checklist is a report — while creating, activating and deactivating
 * are `engineer`. The tier is not enforced here, the API decides; but every
 * control that calls a write below must gate on the same one, or the UI offers a
 * button that was always going to be refused.
 *
 * The three routes added for the client Components tab all take a tenant, which
 * the first two do not. They still live here rather than under `features/clients`
 * because the write is about the *component* — `PUT /components/:id/activations/
 * :tenantId` — and one write path per row type keeps the audit trail to one
 * shape per action, however the operator got there. That is the same call
 * `feature-flags/api/tenant-flags.ts` makes for overrides.
 */

export async function listComponents(signal?: AbortSignal): Promise<Component[]> {
    const { components } = await apiRequest('/components', ComponentListResponse, { signal });
    return components;
}

export interface CreateComponentInput {
    /** Permanent. Grants are written under it, and nothing renames or reuses one. */
    readonly id: string;
    readonly displayName: string;
    readonly description: string;
    readonly owner: string;
    /**
     * Members. Every one must be a flag whose `purpose` is `entitlement`; the
     * API refuses the rest with `COMPONENT_MEMBER_NOT_ENTITLEMENT`, naming the
     * flag. May be empty — a bundle can be assembled before its flags exist.
     */
    readonly flagKeys: readonly string[];
    readonly ownerService?: string;
    readonly activationPath?: string;
    readonly reason?: string;
}

/**
 * Define a component. Grants nothing: no museum gains a flag, and the row holds
 * no activation state — which is what lets an operator assemble a bundle before
 * deciding who gets it.
 */
export function createComponent(input: CreateComponentInput): Promise<Component> {
    return apiRequest('/components', Component, { method: 'POST', body: input });
}

/* ------------------------------------------------- one museum's holding - */

/**
 * What one museum's activation of one component is worth reporting.
 *
 * A discriminated union rather than `void`, because the route has **two
 * different successes** and the operator must be able to tell them apart:
 *
 * - `{ ok: true }` — 204. Every flag granted, every setting seeded.
 * - `{ ok: 'seed-incomplete' }` — 200 carrying
 *   `COMPONENT_SETTINGS_SEED_INCOMPLETE`. The museum **is** entitled and the
 *   component works; only the configuration checklist is short. The API chose a
 *   success status for exactly this reason, and collapsing it back into `void`
 *   here would throw away the one fact the operator needs in order to go and
 *   finish the seeding — while collapsing it into an error would tell them a
 *   change that landed did not, which is the one mistake that makes an operator
 *   back out a change that was fine.
 *
 * The third outcome is a thrown `ApiError`, and its `code` is the branch point
 * rather than its status: `COMPONENT_ACTIVATION_INCOMPLETE` is a 503 meaning
 * "grants stopped part-way, retrying is safe", and that sentence is true of that
 * code and of no other 503 this route can answer with.
 */
export type ActivationResult =
    { readonly ok: true } | { readonly ok: 'seed-incomplete'; readonly message: string };

/** The code the API answers 200 with when the entitlement landed and the checklist did not. */
const SEED_INCOMPLETE = 'COMPONENT_SETTINGS_SEED_INCOMPLETE';

/**
 * The caveat body, read loosely on purpose.
 *
 * Both fields are optional because this is a *success* body: a 200 that carried
 * something unexpected, or a code this build has never heard of, must still
 * resolve as the activation it was. Only the one code above changes the answer.
 */
const ActivationCaveat = z.object({
    code: z.string().optional(),
    message: z.string().optional(),
});

/** `PUT` and `DELETE` share a path, built once here so the two cannot drift. */
function activationPath(componentId: string, tenantId: string): string {
    return `/components/${encodeURIComponent(componentId)}/activations/${encodeURIComponent(tenantId)}`;
}

/** Omitted rather than sent as `undefined`, so a reasonless write sends no body at all. */
function reasonBody(reason: string | undefined): { body?: unknown } {
    return reason === undefined ? {} : { body: { reason } };
}

/**
 * Grant this museum every flag the component holds, and seed its checklist.
 *
 * Not {@link apiRequestNoContent}, even though the happy path is a 204: that
 * helper discards the `Response`, so the `COMPONENT_SETTINGS_SEED_INCOMPLETE`
 * body would be unreadable through it — and that case is a real one this screen
 * has to render, not a theoretical one. `apiRequest` is no good either, because
 * it calls `response.json()` unconditionally and a 204 has no body to parse. So
 * this goes through `apiRequestMaybeContent`, which was written for exactly this
 * shape, rather than a second copy of `request` out here — duplicating that
 * would duplicate the SigV4 payload header and the 401 handling with it.
 *
 * Idempotent server-side: adding a grantor that is already there adds nothing,
 * which is what lets the 503 above invite a retry.
 */
export async function activateComponent(
    componentId: string,
    tenantId: string,
    reason?: string,
): Promise<ActivationResult> {
    const caveat = await apiRequestMaybeContent(
        activationPath(componentId, tenantId),
        ActivationCaveat,
        { method: 'PUT', ...reasonBody(reason) },
    );

    if (caveat?.code !== SEED_INCOMPLETE) return { ok: true };

    // The API's own sentence, which names how many settings were missed. There
    // is no structured count in the body to rebuild one from.
    return { ok: 'seed-incomplete', message: caveat.message ?? '' };
}

/**
 * Withdraw this component's grants from the museum.
 *
 * Always 204, with no caveat case: the grants come off, and the checklist rows
 * are **kept** — a museum re-enabled a month later does not lose the work it
 * finished, which is why a deactivated museum still reads back a non-empty
 * `settings` list from {@link getComponentSettings}.
 */
export function deactivateComponent(
    componentId: string,
    tenantId: string,
    reason?: string,
): Promise<void> {
    return apiRequestNoContent(activationPath(componentId, tenantId), {
        method: 'DELETE',
        ...reasonBody(reason),
    });
}

/**
 * This museum's checklist for this component.
 *
 * The one read in this file that answers an **unwrapped** object rather than a
 * `{ things: [...] }` envelope: it returns one view and has nothing to sit
 * beside it, where a list leaves room for a cursor. Hence no destructuring here.
 *
 * Tenant-first in the path, because the row is a property of the museum.
 */
export function getComponentSettings(
    tenantId: string,
    componentId: string,
    signal?: AbortSignal,
): Promise<ComponentSettingsView> {
    return apiRequest(
        `/tenants/${encodeURIComponent(tenantId)}/components/${encodeURIComponent(componentId)}/settings`,
        ComponentSettingsView,
        { signal },
    );
}

/**
 * Which components this museum holds.
 *
 * Tenant-first, like its neighbour and for the same reason: the answer is a
 * property of the museum. One call per client page rather than one per
 * component — the inverse route, `/components/:id/subscribers`, would need
 * calling once per catalog row and looping each on its own cursor.
 *
 * Destructured because the body is wrapped; the schema says why.
 */
export async function getTenantComponents(
    tenantId: string,
    signal?: AbortSignal,
): Promise<readonly TenantComponentHolding[]> {
    const { components } = await apiRequest(
        `/tenants/${encodeURIComponent(tenantId)}/components`,
        TenantComponentsView,
        { signal },
    );
    return components;
}
